import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, ActionIcon } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { formatRelative, formatDateTime } from '../../lib/format';
import { StatusPill, type PillTone } from '../../components/panel';
import { HttpAuthFields } from '../flows/builder/components/HttpAuthFields';
import type { JsonObject } from '../flows/builder/mapper';
import { ApiWizard } from './ApiWizard';
import { ApiConnectionsList } from './ApiConnections';

type Source = flows.LiveSource;
type Mode = flows.LiveSourceMode;
const MUTED = 'var(--rr-color-text-muted)';

/** The form's working copy of a source: text boxes hold text, so lists are edited as lines and rows. */
interface Draft {
  name: string;
  answers: string;
  examples: string;
  mode: Mode;
  interval: number;
  method: 'GET' | 'POST';
  url: string;
  body: string;
  auth: JsonObject;
  /** Kept as they came: the form has no editor for them yet. */
  headers?: Record<string, string>;
  parameters: Array<{ name: string; description: string; required: boolean }>;
  /** A data point keeps its name in flows and what it is, when it has them (19 October 2026). */
  paths: Array<{ path: string; label: string; key?: string; description?: string }>;
  active: boolean;
  /** The API connection it signs in through; its url is then a path there. */
  connectionId?: string | null;
  /** Its name in flows: a Look up step keeps its data points under it. */
  key: string;
}

const EMPTY: Draft = {
  name: '', answers: '', examples: '', mode: 'lookup', interval: 60, method: 'GET', url: '', body: '',
  auth: {}, parameters: [], paths: [], active: true, key: ''
};

function toDraft(s: Source): Draft {
  const req = s.request ?? { url: '' };
  return {
    name: s.name,
    answers: s.answers ?? '',
    examples: (s.examples ?? []).join('\n'),
    mode: s.mode,
    interval: s.interval_minutes ?? 60,
    method: (req.method ?? 'GET').toUpperCase() === 'POST' ? 'POST' : 'GET',
    url: req.url ?? '',
    body: typeof req.body === 'string' ? req.body : req.body == null ? '' : JSON.stringify(req.body, null, 2),
    auth: (req.auth as JsonObject | undefined) ?? {},
    headers: req.headers,
    parameters: (s.parameters ?? []).map((p) => ({ name: p.name, description: p.description, required: p.required !== false })),
    paths: (s.response_paths ?? []).map((p) => ({ path: p.path, label: p.label ?? '', key: p.key, description: p.description })),
    active: s.is_active,
    connectionId: s.connection_id ?? null,
    key: s.key ?? ''
  };
}

function toInput(d: Draft): flows.LiveSourceInput {
  const request: flows.LiveSourceHttp = { method: d.method, url: d.url.trim() };
  if (d.headers && Object.keys(d.headers).length > 0) request.headers = d.headers;
  if (d.method === 'POST' && d.body.trim()) request.body = d.body;
  if (!d.connectionId && d.auth.type && d.auth.type !== 'none') request.auth = d.auth;
  return {
    name: d.name.trim(),
    answers: d.answers.trim(),
    examples: d.examples.split('\n').map((e) => e.trim()).filter(Boolean),
    mode: d.mode,
    request,
    parameters: d.mode === 'lookup' ? d.parameters.filter((p) => p.name.trim()).map((p) => ({ name: p.name.trim(), description: p.description.trim(), required: p.required })) : [],
    response_paths: d.paths.filter((p) => p.path.trim()).map((p) => ({
      path: p.path.trim(), label: p.label.trim(), ...(p.key ? { key: p.key } : {}), ...(p.description ? { description: p.description } : {})
    })),
    interval_minutes: d.mode === 'refresh' ? d.interval : null,
    is_active: d.active,
    connection_id: d.connectionId ?? null,
    key: d.key.trim() || null
  };
}

const statusTone = (s: Source): PillTone => (s.status === 'failing' ? 'rejected' : !s.is_active ? 'paused' : s.status === 'ok' ? 'active' : 'submitted');

/**
 * Live sources: knowledge that comes from the business's own system instead of
 * a document (event dates, availability, an order's status). Each source says
 * how it stays fresh, because the cost follows that choice: asked when a
 * customer asks, a copy refreshed on a schedule, or a copy refreshed by hand.
 */
export function LiveSourcesCard() {
  const t = useT();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Source | 'new' | null>(null);
  const [wizard, setWizard] = useState<{ connection: flows.ApiConnection | null } | null>(null);

  const list = useQuery({ queryKey: ['kb-sources'], queryFn: () => flows.listKbSources() });
  const sources = list.data?.data ?? [];
  const apis = useQuery({ queryKey: ['api-connections'], queryFn: () => flows.listApiConnections() });
  const connections = apis.data?.data ?? [];
  const connectionOf = (s: Source) => connections.find((c) => c.id === s.connection_id) ?? null;
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['kb-sources'] });
    void qc.invalidateQueries({ queryKey: ['kb-documents'] });
    void qc.invalidateQueries({ queryKey: ['api-connections'] });
  };

  const fetchNow = useMutation({
    mutationFn: (s: Source) => flows.fetchKbSourceNow(s.id),
    onSuccess: (res, s) => {
      refreshAll();
      if (!res.data.ok) notifyError(res.data.error ?? t('common.unknownError'), s.name);
      else notifySuccess(res.data.changed ? t('knowledge.live.toast.changed') : t('knowledge.live.toast.unchanged'), s.name);
    },
    onError: (e, s) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), s.name)
  });
  const remove = useMutation({
    mutationFn: (s: Source) => flows.deleteKbSource(s.id),
    onSuccess: (_r, s) => { refreshAll(); notifySuccess(t('knowledge.live.toast.removed'), s.name); },
    onError: (e, s) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), s.name)
  });

  return (
    <Card withBorder data-testid="live-sources">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={4}>{t('knowledge.live.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('knowledge.live.intro')}</Text>
          </Stack>
          <Group gap="xs">
            <Button size="xs" onClick={() => setWizard({ connection: null })} data-testid="api-wizard-open">{t('knowledge.api.open')}</Button>
            <Button size="xs" variant="default" onClick={() => setEditing('new')}>{t('knowledge.live.addByHand')}</Button>
          </Group>
        </Group>
        <ApiConnectionsList connections={connections} onAddMore={(c) => setWizard({ connection: c })} onChanged={refreshAll} />
        {list.error && <Alert color="error" role="alert">{(list.error as Error).message}</Alert>}
        {!list.isPending && sources.length === 0 && (
          <EmptyState title={t('knowledge.live.empty')} description={t('knowledge.live.emptyHint')} />
        )}
        {sources.map((s) => (
          <div key={s.id} className="rr-knowledge__source" data-testid="live-source">
            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
              <Group gap="xs" wrap="wrap">
                <Text role="bodySmall" fw={650}>{s.name}</Text>
                <StatusPill tone={statusTone(s)}>{t(!s.is_active ? 'knowledge.live.status.off' : `knowledge.live.status.${s.status}`)}</StatusPill>
                {s.key && <code className="rr-knowledge__key">{`{{${s.key}}}`}</code>}
              </Group>
              {connectionOf(s) && <Text role="caption" c={MUTED}>{t('knowledge.api.via', { name: connectionOf(s)!.name })}</Text>}
              <Text role="caption" c={MUTED}>
                {s.mode === 'refresh'
                  ? t('knowledge.live.modeLine.refresh', { every: t(`knowledge.live.every.${s.interval_minutes ?? 60}`) })
                  : t(`knowledge.live.modeLine.${s.mode}`)}
                {s.last_fetched_at ? ` · ${t('knowledge.live.lastFetched', { when: formatRelative(s.last_fetched_at) })}` : ''}
                {s.mode !== 'lookup' && s.last_changed_at ? ` · ${t('knowledge.live.lastChanged', { when: formatDateTime(s.last_changed_at) })}` : ''}
              </Text>
              {s.status === 'failing' && s.last_error && <Text role="caption" c="var(--rr-color-error, #dc2626)">{s.last_error}</Text>}
            </Stack>
            <Group gap={4} wrap="nowrap">
              {s.mode !== 'lookup' && (
                <Tooltip label={t('knowledge.live.fetchNowHint')} position="top">
                  <Button size="xs" variant="default" loading={fetchNow.isPending && fetchNow.variables?.id === s.id} onClick={() => fetchNow.mutate(s)}>{t('knowledge.live.fetchNow')}</Button>
                </Tooltip>
              )}
              <Button size="xs" variant="subtle" onClick={() => setEditing(s)}>{t('action.edit')}</Button>
              <ActionIcon
                variant="subtle" size="sm" color="error"
                aria-label={t('knowledge.live.remove', { name: s.name })}
                onClick={() => openConfirmModal({
                  title: t('knowledge.live.removeTitle'),
                  children: <Text role="bodySmall">{t(s.mode === 'lookup' ? 'knowledge.live.removeBody' : 'knowledge.live.removeBodyCopy', { name: s.name })}</Text>,
                  labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(s)
                })}
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M2.8 4.2h10.4M6.2 4.2V2.8h3.6v1.4M4.2 4.2l.6 9h6.4l.6-9M6.6 6.8v4M9.4 6.8v4" />
                </svg>
              </ActionIcon>
            </Group>
          </div>
        ))}
      </Stack>
      <LiveSourceForm source={editing} connections={connections} onClose={() => setEditing(null)} onSaved={refreshAll} />
      <ApiWizard opened={wizard !== null} connection={wizard?.connection ?? null} onClose={() => setWizard(null)} onDone={refreshAll} />
    </Card>
  );
}

function LiveSourceForm({ source, connections, onClose, onSaved }: { source: Source | 'new' | null; connections: flows.ApiConnection[]; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const existing = source && source !== 'new' ? source : null;
  const [d, setD] = useState<Draft>(EMPTY);
  const [question, setQuestion] = useState('');
  const [test, setTest] = useState<flows.LiveSourceTest | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const set = (changes: Partial<Draft>) => setD((prev) => ({ ...prev, ...changes }));

  useEffect(() => {
    if (source === null) return;
    setD(existing ? toDraft(existing) : EMPTY);
    setQuestion(''); setTest(null); setTestError(null); setSaveError(null);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => (existing ? flows.updateKbSource(existing.id, toInput(d)) : flows.createKbSource(toInput(d))),
    onSuccess: (res) => {
      onSaved();
      const s = res.data;
      if (s.status === 'failing') notifyError(s.last_error ?? t('common.unknownError'), s.name);
      else notifySuccess(t(existing ? 'knowledge.live.toast.saved' : 'knowledge.live.toast.added'), s.name);
      onClose();
    },
    onError: (e) => setSaveError(e instanceof Error ? e.message : t('common.unknownError'))
  });

  async function runTest() {
    setTesting(true); setTestError(null);
    try {
      const res = await flows.testKbSource({ id: existing?.id, source: toInput(d), question: d.mode === 'lookup' && question.trim() ? question.trim() : undefined });
      setTest(res.data);
    } catch (e) {
      setTest(null);
      setTestError(e instanceof Error ? e.message : t('common.unknownError'));
    } finally { setTesting(false); }
  }

  const ready = d.name.trim().length > 0 && d.url.trim().length > 0;
  const connection = d.connectionId ? connections.find((c) => c.id === d.connectionId) ?? null : null;
  const checksPerDay = Math.round(1440 / Math.max(1, d.interval));

  return (
    <Drawer opened={source !== null} onClose={onClose} title={existing ? t('knowledge.live.form.edit') : t('knowledge.live.form.new')} position="right" size="lg">
      <Stack gap="md" style={{ padding: 4 }} data-testid="live-source-form">
        <TextInput label={t('knowledge.live.form.name')} description={t('knowledge.live.form.nameHint')} value={d.name} maxLength={120} onChange={(e) => set({ name: e.currentTarget.value })} />
        <Textarea label={t('knowledge.live.form.answers')} description={t('knowledge.live.form.answersHint')} value={d.answers} minRows={2} maxLength={500} onChange={(e) => set({ answers: e.currentTarget.value })} />

        <Select
          label={t('knowledge.live.form.mode')}
          description={d.mode === 'refresh' ? t('knowledge.live.cost.refresh', { checks: checksPerDay }) : t(`knowledge.live.cost.${d.mode}`)}
          value={d.mode}
          onChange={(e) => set({ mode: e.currentTarget.value as Mode })}
          data={[
            { value: 'lookup', label: t('knowledge.live.mode.lookup') },
            { value: 'refresh', label: t('knowledge.live.mode.refresh') },
            { value: 'manual', label: t('knowledge.live.mode.manual') }
          ]}
        />
        {d.mode === 'refresh' && (
          <Select
            label={t('knowledge.live.form.interval')}
            value={String(d.interval)}
            onChange={(e) => set({ interval: Number(e.currentTarget.value) })}
            data={flows.LIVE_SOURCE_INTERVALS.map((m) => ({ value: String(m), label: t(`knowledge.live.every.${m}`) }))}
          />
        )}
        {d.mode === 'lookup' && (
          <Textarea label={t('knowledge.live.form.examples')} description={t('knowledge.live.form.examplesHint')} value={d.examples} minRows={3} onChange={(e) => set({ examples: e.currentTarget.value })} />
        )}

        <div className="rr-knowledge__method">
          <Select label={t('knowledge.live.form.method')} value={d.method} onChange={(e) => set({ method: e.currentTarget.value as 'GET' | 'POST' })} data={[{ value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' }]} />
          <TextInput
            label={t(d.connectionId ? 'knowledge.api.form.path' : 'knowledge.live.form.url')}
            description={d.connectionId
              ? t('knowledge.api.form.pathHint', { base: connection?.base_url ?? '', example: '{{order}}' })
              : d.mode === 'lookup' ? t('knowledge.live.form.urlHintLookup', { order: '{{order}}' }) : t('knowledge.live.form.urlHint')}
            value={d.url}
            placeholder={d.connectionId ? '/orders/{{order}}' : 'https://api.example.co.za/orders/{{order}}'}
            onChange={(e) => set({ url: e.currentTarget.value })}
          />
        </div>
        {d.method === 'POST' && (
          <Textarea label={t('knowledge.live.form.body')} description={t('knowledge.live.form.bodyHint')} value={d.body} minRows={3} onChange={(e) => set({ body: e.currentTarget.value })} />
        )}
        {d.connectionId ? (
          <Text role="caption" c={MUTED}>{t('knowledge.api.form.signsIn', { name: connection?.name ?? '' })}</Text>
        ) : (
          <HttpAuthFields
            auth={d.auth}
            onChange={(next) => set({ auth: next ?? {} })}
            t={t}
            secretHint={t('knowledge.live.form.secretHint')}
            saved={existing && existing.request?.auth?.type === d.auth.type ? existing.secret_fields : []}
            savedPlaceholder={t('knowledge.live.form.secretSaved')}
          />
        )}
        <TextInput label={t('knowledge.api.review.key')} description={t('knowledge.api.form.keyHint')} value={d.key} maxLength={40}
          placeholder={'order_status' /* i18n-ignore: an example name */} onChange={(e) => set({ key: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />

        {d.mode === 'lookup' && (
          <Stack gap={6}>
            <Text role="inputLabel">{t('knowledge.live.form.details')}</Text>
            <Text role="caption" c={MUTED}>{t('knowledge.live.form.detailsHint')}</Text>
            {d.parameters.map((p, i) => (
              <div key={i} className="rr-knowledge__detail">
                <TextInput aria-label={t('knowledge.live.form.detailName')} placeholder={'order' /* i18n-ignore: a field name */} value={p.name}
                  onChange={(e) => set({ parameters: d.parameters.map((x, j) => (j === i ? { ...x, name: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') } : x)) })} />
                <TextInput aria-label={t('knowledge.live.form.detailDescription')} placeholder={t('knowledge.live.form.detailPlaceholder')} value={p.description}
                  onChange={(e) => set({ parameters: d.parameters.map((x, j) => (j === i ? { ...x, description: e.currentTarget.value } : x)) })} />
                <ActionIcon variant="subtle" size="sm" aria-label={t('knowledge.live.form.detailRemove')} onClick={() => set({ parameters: d.parameters.filter((_x, j) => j !== i) })}>×</ActionIcon>
              </div>
            ))}
            {d.parameters.length < 5 && (
              <Button size="xs" variant="default" style={{ alignSelf: 'flex-start' }} onClick={() => set({ parameters: [...d.parameters, { name: '', description: '', required: true }] })}>{t('knowledge.live.form.detailAdd')}</Button>
            )}
          </Stack>
        )}

        <Stack gap={6}>
          <Text role="inputLabel">{t('knowledge.live.form.paths')}</Text>
          <Text role="caption" c={MUTED}>{t('knowledge.live.form.pathsHint')}</Text>
          {d.paths.map((p, i) => (
            <div key={i} className="rr-knowledge__detail">
              <TextInput aria-label={t('knowledge.live.form.pathPath')} placeholder={'status' /* i18n-ignore: a field name */} value={p.path}
                onChange={(e) => set({ paths: d.paths.map((x, j) => (j === i ? { ...x, path: e.currentTarget.value } : x)) })} />
              <TextInput aria-label={t('knowledge.live.form.pathLabel')} placeholder={t('knowledge.live.form.pathLabelPlaceholder')} value={p.label}
                onChange={(e) => set({ paths: d.paths.map((x, j) => (j === i ? { ...x, label: e.currentTarget.value } : x)) })} />
              <ActionIcon variant="subtle" size="sm" aria-label={t('knowledge.live.form.pathRemove')} onClick={() => set({ paths: d.paths.filter((_x, j) => j !== i) })}>×</ActionIcon>
            </div>
          ))}
          <Button size="xs" variant="default" style={{ alignSelf: 'flex-start' }} onClick={() => set({ paths: [...d.paths, { path: '', label: '' }] })}>{t('knowledge.live.form.pathAdd')}</Button>
        </Stack>

        <div className="rr-knowledge__test" data-testid="live-source-test">
          <Text role="inputLabel">{t('knowledge.live.test.title')}</Text>
          <Text role="caption" c={MUTED}>{t(d.mode === 'lookup' ? 'knowledge.live.test.hintLookup' : 'knowledge.live.test.hint')}</Text>
          <Group gap="xs" align="flex-end" wrap="nowrap">
            {d.mode === 'lookup' && (
              <TextInput aria-label={t('knowledge.live.test.question')} placeholder={t('knowledge.live.test.questionPlaceholder')} value={question} onChange={(e) => setQuestion(e.currentTarget.value)} style={{ flex: 1 }} />
            )}
            <Button size="xs" variant="default" loading={testing} disabled={!ready} onClick={runTest}>{t('knowledge.live.test.run')}</Button>
          </Group>
          {testError && <Text role="caption" c="var(--rr-color-error, #dc2626)">{testError}</Text>}
          {test && (
            <Stack gap={6}>
              {!test.ok && <Text role="bodySmall" c="var(--rr-color-error, #dc2626)">{test.error ?? t('common.unknownError')}</Text>}
              {test.score != null && (
                <Text role="caption" c={MUTED}>
                  {t(test.matched ? 'knowledge.live.test.matched' : 'knowledge.live.test.notMatched', { score: Math.round(test.score * 100), needed: Math.round(test.match_at * 100) })}
                </Text>
              )}
              {Object.keys(test.values).length > 0 && (
                <Text role="caption" c={MUTED}>{t('knowledge.live.test.values', { values: Object.entries(test.values).map(([k, v]) => `${k} = ${v}`).join(', ') })}</Text>
              )}
              {test.missing.length > 0 && <Text role="caption" c={MUTED}>{t('knowledge.live.test.missing', { names: test.missing.join(', ') })}</Text>}
              {test.passage && (
                <>
                  <Text role="caption" fw={650}>{t('knowledge.live.test.passage')}</Text>
                  <pre className="rr-knowledge__passage">{test.passage}</pre>
                </>
              )}
              {test.paths.length > 0 && (
                <details>
                  <summary>{t('knowledge.live.test.paths', { count: test.paths.length })}</summary>
                  <Stack gap={2}>
                    {test.paths.slice(0, 60).map((p) => (
                      <button key={p.path} type="button" className="rr-knowledge__path" onClick={() => set({ paths: [...d.paths.filter((x) => x.path), { path: p.path, label: '' }] })}>
                        <code>{p.path}</code><span>{p.sample}</span>
                      </button>
                    ))}
                  </Stack>
                </details>
              )}
            </Stack>
          )}
        </div>

        <Switch label={t('knowledge.live.form.active')} description={t('knowledge.live.form.activeHint')} checked={d.active} onChange={(checked) => set({ active: checked })} />
        {saveError && <Alert color="error" role="alert">{saveError}</Alert>}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={save.isPending} disabled={!ready} onClick={() => { setSaveError(null); save.mutate(); }}>{t(existing ? 'action.save' : 'knowledge.live.add')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
